import * as React from 'react'
import { Check, AlertCircle } from 'lucide-react'
import { cn } from 'src/lib/utils'

type PromoCodeNoticeProps = {
  variant: 'applied' | 'invalid'
  code: string
  discountLabel?: string | null
  // Optional trailing note, e.g. "Monthly plan only".
  scopeNote?: string | null
  // When provided, renders a "Remove" control (applied variant only).
  onRemove?: () => void
  className?: string
}

export const PromoCodeNotice = ({
  variant,
  code,
  discountLabel,
  scopeNote,
  onRemove,
  className,
}: PromoCodeNoticeProps): React.ReactElement => {
  const baseClassName = cn(
    'flex flex-wrap items-center gap-x-2 gap-y-1 font-roboto-condensed text-sm tracking-[0.5px]',
    className,
  )

  if (variant === 'invalid') {
    return (
      <p role="alert" className={cn(baseClassName, 'text-error-2 font-semibold')}>
        <AlertCircle className="h-4 w-4 shrink-0" aria-hidden="true" />
        <span>
          Promo <strong className="font-bold uppercase">{code}</strong> is invalid or expired
        </span>
      </p>
    )
  }

  return (
    <p role="status" className={cn(baseClassName, 'text-ivory')}>
      <Check className="h-4 w-4 shrink-0 text-green-09" aria-hidden="true" />
      <span>
        Promo <strong className="font-bold uppercase text-secondary">{code}</strong> applied
        {discountLabel ? <span className="text-mushroom">{` — ${discountLabel}`}</span> : null}
        {scopeNote ? <span className="text-mushroom">{` · ${scopeNote}`}</span> : null}
      </span>
      {onRemove ? (
        <button
          type="button"
          onClick={onRemove}
          className="text-mushroom underline underline-offset-2 hover:text-ivory transition-colors"
        >
          Remove
        </button>
      ) : null}
    </p>
  )
}
